import React from 'react';
import { ExternalLink, RefreshCw, X } from 'lucide-react';

interface AdaptiveCardAction {
  type: string;
  data: {
    action: string;
  };
  title: string;
  style?: string;
}

interface AdaptiveCardContent {
  type: string;
  version: string;
  body: Array<{
    type: string;
    text?: string;
    wrap?: boolean;
    columns?: Array<{
      type: string;
      width: string;
      items: Array<{
        type: string;
        actions?: AdaptiveCardAction[];
      }>;
    }>;
  }>;
}

interface ConnectionManagerCardProps {
  content: AdaptiveCardContent;
  onRetry?: () => void;
  onCancel?: () => void;
}

const ConnectionManagerCard: React.FC<ConnectionManagerCardProps> = ({
  content,
  onRetry,
  onCancel
}) => {
  const handleOpenConnectionManager = () => {
    // Extract URL from the text content
    const textContent = content.body.find(item => item.type === 'TextBlock' && item.text)?.text || '';
    const urlMatch = textContent.match(/\[Open connection manager\]\((https?:\/\/[^\s)]+)\)/);
    
    if (urlMatch && urlMatch[1]) {
      window.open(urlMatch[1], '_blank');
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="bg-white border border-orange-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <ExternalLink size={16} className="text-orange-600" />
          </div>
        </div>
        
        <div className="flex-1 space-y-3">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Connection Required
            </h3>
            
            {content.body.map((item, index) => {
              if (item.type === 'TextBlock' && item.text) {
                // Remove the markdown link and display clean text
                const cleanText = item.text.replace(/\[Open connection manager\]\([^)]+\)/, 'Open connection manager');
                
                return (
                  <p key={index} className="text-sm text-gray-600 mb-2 leading-relaxed">
                    {cleanText}
                  </p>
                );
              }
              return null;
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleOpenConnectionManager}
              className="inline-flex items-center justify-center px-3 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
            >
              <ExternalLink size={14} className="mr-2" />
              Open Connection Manager
            </button>
            
            <button
              onClick={handleRetry}
              className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            >
              <RefreshCw size={14} className="mr-2" />
              Retry
            </button>
            
            <button
              onClick={handleCancel}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              <X size={14} className="mr-2" />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionManagerCard; 